import { Controller } from "@hotwired/stimulus"

const DAY_DEFAULTS = {
  business_hours: [1, 2, 3, 4, 5],
  outside_hours: [0, 1, 2, 3, 4, 5, 6],
}

const TIME_DEFAULTS = {
  business_hours: { start: "09:00", end: "17:00" },
  outside_hours: { start: "17:00", end: "22:00" },
}

export default class extends Controller {
  static targets = ["timeRestriction", "customHours", "startLabel", "endLabel"]

  connect() {
    this.initialized = false
    this.previousValue = this.currentValue()
    this.toggle()
    this.initialized = true
  }

  toggle() {
    const value = this.currentValue()
    const show = ["custom", "business_hours", "outside_hours"].includes(value)

    this.customHoursTargets.forEach((element) => {
      element.classList.toggle("hidden", !show)
      element.toggleAttribute("hidden", !show)
    })

    if (show) {
      this.applyDefaults(value)
    } else {
      this.clearRequirements()
    }

    this.updateLabels(value)
    this.previousValue = value
  }

  currentValue() {
    return this.hasTimeRestrictionTarget ? this.timeRestrictionTarget.value : null
  }

  applyDefaults(value) {
    if (value === "custom") {
      if (this.previousValue && this.previousValue !== "custom") {
        this.clearCustomHours()
      }
      return
    }

    const timeDefaults = TIME_DEFAULTS[value]
    const dayDefaults = DAY_DEFAULTS[value]

    if (timeDefaults) {
      this.startInputs().forEach((input) => {
        if (this.shouldApplyTimeDefaults(input)) this.setDisplayTime(input, timeDefaults.start)
      })
      this.endInputs().forEach((input) => {
        if (this.shouldApplyTimeDefaults(input)) this.setDisplayTime(input, timeDefaults.end)
      })
    }

    if (dayDefaults) {
      if (this.shouldApplyDayDefaults()) {
        this.dayCheckboxes().forEach((checkbox) => {
          const dayValue = parseInt(checkbox.value, 10)
          checkbox.checked = dayDefaults.includes(dayValue)
        })
      }
    }
  }

  clearCustomHours() {
    this.startInputs().forEach((input) => this.setDisplayTime(input, null))
    this.endInputs().forEach((input) => this.setDisplayTime(input, null))
    this.dayCheckboxes().forEach((checkbox) => {
      checkbox.checked = false
    })
  }

  clearRequirements() {
    this.customHoursTargets.forEach((element) => {
      element.querySelectorAll("input[required], select[required]").forEach((node) => {
        node.required = false
      })
    })
  }

  updateLabels(value) {
    const outside = value === "outside_hours"
    this.updateLabelCollection(this.startLabelTargets, outside)
    this.updateLabelCollection(this.endLabelTargets, outside)
  }

  updateLabelCollection(labels, outside) {
    labels.forEach((label) => {
      const text = outside ? label.dataset.outsideText || label.dataset.defaultText : label.dataset.defaultText
      if (!text) return
      this.setLabelText(label, text)
    })
  }

  setLabelText(label, text) {
    const textNode = Array.from(label.childNodes).find((node) => node.nodeType === Node.TEXT_NODE)
    if (textNode) {
      textNode.textContent = `${text} `
    } else {
      label.insertBefore(document.createTextNode(`${text} `), label.firstChild || null)
    }
  }

  startInputs() {
    return this.customHoursTargets.flatMap((element) =>
      Array.from(element.querySelectorAll("input[name$='[start_time]']")),
    )
  }

  endInputs() {
    return this.customHoursTargets.flatMap((element) =>
      Array.from(element.querySelectorAll("input[name$='[end_time]']")),
    )
  }

  shouldApplyDayDefaults() {
    const checkboxes = this.dayCheckboxes()
    return checkboxes.length > 0 && !checkboxes.some((checkbox) => checkbox.checked)
  }

  shouldApplyTimeDefaults(input) {
    return !input.value || input.value.trim() === ""
  }

  dayCheckboxes() {
    return this.customHoursTargets.flatMap((element) =>
      Array.from(element.querySelectorAll("input[type='checkbox'][name$='[days][]']")),
    )
  }

  setDisplayTime(input, timeString) {
    const picker = input._flatpickr

    if (!timeString) {
      input.value = ""
      if (picker) picker.clear()
      input.dispatchEvent(new Event("change", { bubbles: true }))
      return
    }

    const formatted = this.toMeridiemTime(timeString)
    input.value = formatted

    const dateValue = this.parseTimeToDate(formatted)
    if (picker && dateValue) {
      picker.setDate(dateValue, false)
    }

    input.dispatchEvent(new Event("change", { bubbles: true }))
  }

  toMeridiemTime(value) {
    if (!value) return ""

    const trimmed = value.trim()
    if (/[ap]m$/i.test(trimmed)) {
      return trimmed.toUpperCase()
    }

    const [rawHours, minutes] = trimmed.split(":")
    if (rawHours === undefined || minutes === undefined) return trimmed

    let hours = parseInt(rawHours, 10)
    const period = hours >= 12 ? "PM" : "AM"

    hours = hours % 12
    hours = hours === 0 ? 12 : hours

    const paddedHours = hours.toString().padStart(2, "0")
    const paddedMinutes = minutes.padStart(2, "0")

    return `${paddedHours}:${paddedMinutes} ${period}`
  }

  parseTimeToDate(value) {
    if (!value) return null

    const date = new Date()
    date.setSeconds(0)
    date.setMilliseconds(0)

    const meridiemMatch = value.match(/^(\d{1,2}):(\d{2})\s*([ap]m)$/i)
    if (meridiemMatch) {
      let hours = parseInt(meridiemMatch[1], 10)
      const minutes = parseInt(meridiemMatch[2], 10)
      const meridiem = meridiemMatch[3].toLowerCase()

      hours = hours % 12
      if (meridiem === "pm") hours += 12

      date.setHours(hours)
      date.setMinutes(minutes)
      return date
    }

    const twentyFourMatch = value.match(/^(\d{1,2}):(\d{2})$/)
    if (twentyFourMatch) {
      date.setHours(parseInt(twentyFourMatch[1], 10))
      date.setMinutes(parseInt(twentyFourMatch[2], 10))
      return date
    }

    return null
  }
}
